import { useEffect, useRef, useState } from "react";
import { AnimatePresence, m } from "motion/react";
import type { Comment, Media } from "@shared";
import { CountUp, springs } from "../web/src/lib/motion.js";
import { api, ApiError } from "../web/src/lib/api.js";
import { deviceKey } from "../web/src/lib/device.js";
import { guestErrorMessage, localeFor, type GuestLocale } from "../web/src/lib/i18n.js";

/** Host mounts pass nothing and stay English, as the dashboard does. */
const ENGLISH = localeFor("en");

/**
 * Which comments this browser wrote.
 *
 * sessionStorage, not a prop and not the server's say-so. The server cannot tell:
 * `Comment` carries no guest key, because sending one would put it in every
 * gallery response. That left `useState` alone, which meant the Delete control
 * existed only for the minute after posting — close the lightbox, reopen it, and
 * a guest's own comment had become undeletable with no error and no explanation.
 *
 * sessionStorage rather than localStorage because this is per-tab state about a
 * guest at an event, not a durable preference, and it should not outlive the
 * evening on a phone the host lends to the next table.
 */
const OWN_COMMENTS_KEY = "eg_own_comments";

function readOwnComments(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(OWN_COMMENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

function rememberOwnComment(id: string): void {
  try {
    const next = [...readOwnComments(), id];
    window.sessionStorage.setItem(OWN_COMMENTS_KEY, JSON.stringify(next.slice(-200)));
  } catch {
    // Storage refused: the in-memory set still covers this visit.
  }
}

function forgetOwnComment(id: string): void {
  try {
    const next = [...readOwnComments()].filter((existing) => existing !== id);
    window.sessionStorage.setItem(OWN_COMMENTS_KEY, JSON.stringify(next));
  } catch {
    // As above.
  }
}

interface Props {
  readonly media: Media;
  readonly allowComments: boolean;
  readonly allowLikes: boolean;
  readonly guestCode?: string;
  /** Present for a host, who may delete any comment. */
  readonly isHost: boolean;
  readonly onClose: () => void;
  readonly onChange: (media: Media) => void;
  readonly onDeleted: (commentId: string) => void;
  /** Arrow-key navigation. Omitted where there is no sibling to move to. */
  readonly onPrev?: () => void;
  readonly onNext?: () => void;
  /** The guest's language. Omitted on host surfaces. */
  readonly locale?: GuestLocale;
}

/**
 * The lightbox.
 *
 * Keyboard contract: Escape closes, the arrow keys move between photographs, and
 * focus lands inside the dialog on open so a keyboard user is not left tabbing
 * through the page behind it. Tab is deliberately *not* trapped — the dialog has
 * a bounded number of controls, and trapping without a full focus-management
 * implementation makes the exit worse rather than the entry better.
 */
export function Lightbox({
  media,
  allowComments,
  allowLikes,
  guestCode,
  isHost,
  onClose,
  onChange,
  onDeleted,
  onPrev,
  onNext,
  locale = ENGLISH,
}: Props) {
  const { copy } = locale;
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [ownComments, setOwnComments] = useState<Set<string>>(readOwnComments);
  const [removing, setRemoving] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(media.likes);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Which way the last move went, so the next photo slides in from that side.
  const [direction, setDirection] = useState(0);
  // A big heart over the photo after a double-tap; the key restarts it.
  const [burst, setBurst] = useState(0);
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);

  const go = (delta: -1 | 1): void => {
    const move = delta < 0 ? onPrev : onNext;
    if (!move) return;
    setDirection(delta);
    move();
  };

  // Width of the photo area, for the swipe threshold (a third of it).
  const slideWidth = useRef(320);

  // A new photograph starts with its own count; the like state is per photo.
  // Keyed on the id alone: a like updates media.likes through onChange, and
  // resetting on that would un-light the heart the guest just lit.
  useEffect(() => {
    setLikes(media.likes);
    setLiked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.mediaId]);

  useEffect(() => {
    // Focus the close button, not the dialog. Moving focus into a container
    // without a tabindex is a no-op in most browsers, and a keyboard user who
    // opens a photo with Enter should end up somewhere they can act.
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      // Arrow keys must not fight a textarea the guest is typing a comment in.
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      if (event.key === "ArrowLeft" && onPrev) {
        setDirection(-1);
        onPrev();
      }
      if (event.key === "ArrowRight" && onNext) {
        setDirection(1);
        onNext();
      }
    };

    document.addEventListener("keydown", onKey);
    // Stop the page behind the overlay from scrolling under the user's finger.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    if (!allowComments) return;
    api
      .get<{ comments: Comment[] }>(`/api/media/${media.mediaId}/comments`)
      .then((result) => setComments(result.comments))
      .catch(() => setComments([]));
  }, [media.mediaId, allowComments]);

  const send = async (likedNext: boolean): Promise<void> => {
    try {
      const result = await (likedNext
        ? api.post<{ liked: boolean; likes: number }>(`/api/media/${media.mediaId}/like`, {
            guestCode,
            likerKey: deviceKey(),
          })
        : api.del<{ liked: boolean; likes: number }>(`/api/media/${media.mediaId}/like`, {
            guestCode,
            likerKey: deviceKey(),
          }));
      setLiked(result.liked);
      setLikes(result.likes);
      onChange({ ...media, likes: result.likes });
    } catch (err) {
      setError(err instanceof ApiError ? guestErrorMessage(copy, err) : copy.saveFailed);
    }
  };

  /*
   * Double-tap the photo to like it, the gesture every guest already knows.
   * It only ever likes (never un-likes), like the apps it imitates, so a
   * nervous second double-tap cannot take a like back. Detected by hand
   * because mobile Safari does not fire dblclick for a quick double tap.
   */
  const onPhotoTap = (event: React.PointerEvent): void => {
    if (!allowLikes) return;
    const now = performance.now();
    const previous = lastTap.current;
    const near = previous && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) < 30;
    if (previous && now - previous.at < 300 && near) {
      lastTap.current = null;
      setBurst((n) => n + 1);
      if (!liked) void send(true);
      return;
    }
    lastTap.current = { at: now, x: event.clientX, y: event.clientY };
  };

  const post = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;

    setError("");
    try {
      const result = await api.post<{ comment: Comment }>(`/api/media/${media.mediaId}/comments`, {
        body,
        guestName: name.trim() || undefined,
        guestCode,
        likerKey: deviceKey(),
      });
      setComments((previous) => [...previous, result.comment]);
      setOwnComments((previous) => {
        const next = new Set([...previous, result.comment.commentId]);
        rememberOwnComment(result.comment.commentId);
        return next;
      });
      setDraft("");
    } catch (err) {
      setError(err instanceof ApiError ? guestErrorMessage(copy, err) : copy.postFailed);
    }
  };

  const remove = async (commentId: string): Promise<void> => {
    // Say so while it happens. The comment vanishing from the list is the only
    // feedback there was, and a guest who tapped Delete and saw nothing move has
    // no way to tell a slow request from a failed one — so they tap it again.
    setRemoving(commentId);
    setError("");
    try {
      await api.del(`/api/comments/${commentId}`, { guestCode, likerKey: deviceKey() });
      setComments((previous) => previous.filter((c) => c.commentId !== commentId));
      setOwnComments((previous) => {
        const next = new Set([...previous]);
        next.delete(commentId);
        forgetOwnComment(commentId);
        return next;
      });
      onDeleted(commentId);
    } catch (err) {
      setError(err instanceof ApiError ? guestErrorMessage(copy, err) : copy.deleteFailed);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={copy.photoDialog} onClick={onClose}>
      <m.div
        className="lightbox-panel lightbox-panel-motion"
        onClick={(event) => event.stopPropagation()}
        initial={{ opacity: 0, scale: 0.94, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={springs.soft}
      >
        <div className="lightbox-media">
          {/*
            The photo slides in from the side the guest moved towards. On a
            phone it can be dragged: a flick or a drag past a third of the way
            moves to the next photograph, anything less springs back.
          */}
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <m.div
              key={media.mediaId}
              className="lightbox-slide"
              custom={direction}
              variants={{
                enter: (d: number) => ({ x: d === 0 ? 0 : d > 0 ? "60%" : "-60%", opacity: 0 }),
                center: { x: 0, opacity: 1 },
                leave: (d: number) => ({ x: d === 0 ? 0 : d > 0 ? "-60%" : "60%", opacity: 0 }),
              }}
              initial="enter"
              animate="center"
              exit="leave"
              transition={springs.soft}
              drag={onPrev || onNext ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.6}
              onDragEnd={(_, info) => {
                const width = slideWidth.current / 3;
                if (info.offset.x < -width || info.velocity.x < -600) go(1);
                else if (info.offset.x > width || info.velocity.x > 600) go(-1);
              }}
              onPointerUp={onPhotoTap}
              ref={(node: HTMLDivElement | null) => {
                if (node) slideWidth.current = node.offsetWidth || 320;
              }}
            >
              {media.mimeType.startsWith("video/") ? (
                <video src={media.displayUrl} controls autoPlay playsInline />
              ) : (
                <img src={media.displayUrl} alt={media.caption || copy.eventPhoto} draggable={false} />
              )}
            </m.div>
          </AnimatePresence>

          <AnimatePresence>
            {burst > 0 ? (
              <m.span
                key={burst}
                className="like-burst"
                aria-hidden="true"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: [0, 1.25, 1], opacity: [0, 1, 1] }}
                exit={{ scale: 1.4, opacity: 0, transition: { duration: 0.25 } }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                onAnimationComplete={() => setBurst(0)}
              >
                ♥
              </m.span>
            ) : null}
          </AnimatePresence>
        </div>

        <div className="lightbox-side">
          <div className="row lightbox-head">
            <strong className="lightbox-title">{media.caption || copy.untitled}</strong>
            <div className="row">
              {onPrev ? (
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => go(-1)}
                  aria-label={copy.previousPhoto}
                >
                  ←
                </button>
              ) : null}
              {onNext ? (
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => go(1)}
                  aria-label={copy.nextPhoto}
                >
                  →
                </button>
              ) : null}
              <button ref={closeRef} type="button" className="button button-secondary" onClick={onClose}>
                {copy.close}
              </button>
            </div>
          </div>

          {media.guestName ? <p className="muted">{locale.t("sharedBy", { name: media.guestName })}</p> : null}

          {/*
            AI output is labelled rather than mixed into the guest's words.
            A model describing a photograph is useful for search and for a
            screen reader; it is not the photographer's caption, and presenting
            it as one would quietly put words in somebody's mouth.
          */}
          {media.tags.length > 0 ? (
            <div className="tag-filter" aria-label={copy.photoTags}>
              {media.tags.map((value) => (
                <span key={value} className="tag-chip tag-chip-static">
                  {value}
                </span>
              ))}
            </div>
          ) : null}

          {media.aiCaption && media.aiCaption !== media.caption ? (
            <p className="muted ai-caption">
              <span className="ai-label">{copy.autoDescription}</span> {media.aiCaption}
            </p>
          ) : null}

          {media.aiSafe === false ? (
            <p className="error ai-flag">{copy.aiFlagged}</p>
          ) : null}

          {allowLikes ? (
            <div className="row">
              <button
                type="button"
                className={`like-button${liked ? " like-button-on" : ""}`}
                aria-pressed={liked}
                onClick={() => void send(!liked)}
              >
                {/* The heart pops each time it lights up (keyed on the state). */}
                <m.span
                  key={liked ? "on" : "off"}
                  className="like-heart"
                  aria-hidden="true"
                  initial={liked ? { scale: 0.4 } : false}
                  animate={{ scale: [liked ? 0.4 : 1, liked ? 1.35 : 1, 1] }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  {liked ? "♥" : "♡"}
                </m.span>
                {liked ? copy.liked : copy.like}
                <span className="like-count">
                  <CountUp value={likes} />
                </span>
              </button>
              {!liked ? <span className="muted like-hint">{copy.doubleTapToLike}</span> : null}
            </div>
          ) : null}

          {allowComments ? (
            <div className="stack" style={{ gap: "0.6rem" }}>
              <h3>{copy.comments} {comments.length ? `(${comments.length})` : ""}</h3>

              {comments.length === 0 ? (
                <p className="muted">{copy.noComments}</p>
              ) : (
                <ul className="comment-list">
                  {comments.map((comment) => {
                    const mine = ownComments.has(comment.commentId);
                    const canDelete = isHost || mine;
                    return (
                      <li key={comment.commentId}>
                        <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
                          <strong className="comment-author">
                            {comment.author}
                            {mine && !isHost ? <span className="comment-mine"> {copy.you}</span> : null}
                          </strong>
                          {/*
                            A "Delete" link in the corner of a row reads as a label
                            until you know what it does, and to a screen reader it
                            was an unlabelled control in a list of eleven others
                            named identically. Both of those make it the button a
                            guest does not press — which is the wrong button to
                            lose, because it is the only way they take back a
                            comment they regret.

                            So: named after what it will delete, always. And
                            visible, rather than appearing on hover, because this
                            list is scrolled and tapped on a phone.
                          */}
                          {canDelete ? (
                            <button
                              type="button"
                              className="link-button danger"
                              onClick={() => void remove(comment.commentId)}
                              // Disabled only for the row being removed. A global
                              // lock would make the list feel broken mid-scroll.
                              disabled={removing === comment.commentId}
                              aria-label={locale.t("deleteCommentFrom", { name: comment.author })}
                            >
                              {removing === comment.commentId ? copy.deleting : copy.delete}
                            </button>
                          ) : null}
                        </div>
                        <span className="comment-body">{comment.body}</span>
                      </li>
                    );
                  })}
                </ul>
              )}

              <form onSubmit={post} className="stack" style={{ gap: "0.5rem" }}>
                <label htmlFor="comment-body">{copy.addComment}</label>
                <textarea
                  id="comment-body"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder={copy.commentPlaceholder}
                />
                <label htmlFor="comment-name">{copy.namePlaceholder}</label>
                <input
                  id="comment-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={40}
                  autoComplete="name"
                />
                {error ? (
                  <p className="error" role="alert">
                    {error}
                  </p>
                ) : null}
                <button type="submit" disabled={!draft.trim()}>
                  {copy.post}
                </button>
              </form>
            </div>
          ) : null}

          {isHost ? <p className="muted">{copy.hostCanRemove}</p> : null}
        </div>
      </m.div>
    </div>
  );
}
